<script setup lang="ts">
import { collapseFacetOptions, filterFacetOptions } from '~/utils/filter/facet'

const props = withDefaults(defineProps<{
  label: string
  name: string
  options: CheckboxOption[]
  selected: string[]
  toggle: (value: string, checked: boolean) => void
  searchable?: boolean
  isMobile?: boolean
  expandNoun: string
}>(), {
  searchable: true,
})

const search = defineModel<string>('search', { default: '' })

const showAll = ref<boolean>(false)
const showMoreRef = useTemplateRef<ComponentPublicInstance | null>('showMore')

const filteredOptions = computed(() => filterFacetOptions(props.options, search.value))
const totalOptions = computed(() => filteredOptions.value.length)
const showExpandButton = computed(() => totalOptions.value > VISIBLE_FILTER_ITEMS_THRESHOLD)
const visibleOptions = computed(() => showAll.value
  ? filteredOptions.value
  : collapseFacetOptions(filteredOptions.value, props.selected, VISIBLE_FILTER_ITEMS_THRESHOLD))

watch(showExpandButton, (canExpand) => {
  if (!canExpand) {
    showAll.value = false
  }
})

async function toggleShowAll() {
  showAll.value = !showAll.value
  if (showAll.value || props.isMobile) {
    return
  }
  await nextTick()
  showMoreRef.value?.$el?.scrollIntoView({ block: 'nearest' })
}

async function onToggle(value: string, checked: boolean) {
  props.toggle(value, checked)
  if (checked) {
    return
  }
  await nextTick()
  if (!visibleOptions.value.some(option => option.value === value)) {
    showMoreRef.value?.$el?.focus()
  }
}
</script>

<template>
  <div class="w-full flex flex-col">
    <FormSearchInput
      v-if="searchable"
      :id="name"
      v-model="search"
      resettable
      shape="square"
      size="medium"
      :label="`Søk i ${expandNoun}`"
      :disabled="!options.length || options.every(opt => opt.disabled)"
    />
    <fieldset class="w-full flex flex-col gap-s">
      <legend class="sr-only">
        {{ label }}
      </legend>
      <div class="flex flex-col" :class="{ 'gap-2xs': isMobile }">
        <div
          v-for="option in visibleOptions"
          :key="option.value"
          class="flex items-center justify-between gap-s pl-2xs pr-m py-2xs"
        >
          <FormCheckbox
            :label="option.label"
            :name="name"
            class="flex-1"
            :disabled="option.disabled"
            :model-value="selected.includes(option.value)"
            @update:model-value="(checked) => onToggle(option.value, checked)"
          />
          <span v-if="option.count !== undefined" class="text-caption text-secondary shrink-0">
            {{ option.count }}
          </span>
        </div>
      </div>
    </fieldset>
    <FilterShowMoreButton
      ref="showMore"
      v-if="showExpandButton"
      :aria-label="showAll ? `Vis færre ${expandNoun}` : `Vis alle ${expandNoun} (${totalOptions})`"
      :aria-expanded="showAll"
      @click="toggleShowAll"
      class="font-bold pl-2xs"
    >
      Vis {{ showAll ? 'færre' : `alle (${totalOptions})` }}
    </FilterShowMoreButton>
  </div>
</template>